import React from 'react';
import { Box, Grid, Text, VStack } from '@chakra-ui/react';
import { SectionContainer } from '../SectionContainer';
import { ScrollReveal } from '../ScrollReveal';

const cells = [
  {
    label: 'CLOUD-NATIVE',
    description:
      'Every agent is an isolated, independently deployable cloud stack, built as infrastructure-as-code.',
  },
  {
    label: 'ASYNCHRONOUS & SCALABLE',
    description: 'Managed workflows execute complex tasks in parallel, without bottlenecks.',
  },
  {
    label: 'MODEL-AGNOSTIC',
    description:
      'A central router taps any LLM provider and upgrades instantly as new models arrive — continuously optimized by a dedicated agent.',
  },
  {
    label: 'SECURITY FIRST',
    description:
      'Architected to grow fully secured: per-agent isolation, protected networking, and centralized credential management.',
  },
  {
    label: 'VERSION-CONTROLLED TRUTH',
    description: 'Every agent has a single source of truth in code: auditable and reproducible.',
  },
  {
    label: 'SELF-DOCUMENTING',
    description:
      'Every agent generates its own documentation and scope, so the system stays legible as it scales.',
  },
];

function InfraCell({ cell, index }) {
  return (
    <ScrollReveal delay={index * 0.07}>
      <Box
        p={{ base: '20px', md: '24px' }}
        border="1px solid"
        borderColor="whiteAlpha.100"
        borderRadius="12px"
        h="100%"
        _hover={{ borderColor: 'brand.500', bg: 'rgba(99,102,241,0.04)' }}
        sx={{ transition: 'all 0.25s ease' }}
        role="listitem"
      >
        <Text
          fontFamily="'JetBrains Mono', monospace"
          fontWeight="500"
          fontSize="0.72rem"
          textTransform="uppercase"
          letterSpacing="1.5px"
          color="brand.300"
          mb={2}
        >
          {cell.label}
        </Text>
        <Text fontSize="0.92rem" color="text.muted" lineHeight="1.6">
          {cell.description}
        </Text>
      </Box>
    </ScrollReveal>
  );
}

export default function InfrastructureSection() {
  return (
    <SectionContainer bg="bg.800" id="infrastructure" aria-labelledby="infra-heading">
      <VStack spacing={{ base: 8, md: 12 }} align="stretch">
        <ScrollReveal>
          <VStack align="flex-start" spacing={3}>
            <Text
              fontFamily="'JetBrains Mono', monospace"
              fontWeight="500"
              fontSize="0.75rem"
              textTransform="uppercase"
              letterSpacing="1.5px"
              color="brand.300"
            >
              Technical Foundation
            </Text>
            <Text
              id="infra-heading"
              as="h2"
              fontFamily="'Space Grotesk', sans-serif"
              fontWeight="700"
              fontSize={{ base: 'clamp(1.8rem, 5vw, 2rem)', md: 'clamp(2rem, 3.5vw, 2.75rem)' }}
              letterSpacing="-0.02em"
              color="text.primary"
            >
              Enterprise-grade from the first line of code.
            </Text>
          </VStack>
        </ScrollReveal>

        <Grid
          templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
          gap={{ base: 3, md: 4 }}
          role="list"
          aria-label="Infrastructure capabilities"
        >
          {cells.map((cell, i) => (
            <InfraCell key={cell.label} cell={cell} index={i} />
          ))}
        </Grid>
      </VStack>
    </SectionContainer>
  );
}
